import { Component, ChangeDetectionStrategy, inject, signal, computed, input, output, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { from, map } from 'rxjs';
import { RebrandedHairTestimonialObject, RebrandedTestimonialStoriesObject,
  RebrandedTestimonialStory } from 'e2e/src/shared/constants';
import SwiperCore, { SwiperOptions, Navigation, Pagination } from 'swiper';
import { SwiperComponent } from 'swiper/angular';
import { ConnectionService } from '@services/connection-service';
import { UserService } from '@services/user';
import { ExperimentService } from '@services/experiment/experiment.service';
import { TestimonialResponseItem } from '@cureskin/api-client/src/server-api';
import { ApiClientConstant } from '@cureskin/api-client/src/constant/api-client.constant';

SwiperCore.use([Navigation, Pagination]);

@Component({
  selector: 'testimonials-widget',
  templateUrl: './testimonials-widget.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TestimonialsWidgetComponent {
  private readonly swiper = viewChild<SwiperComponent>('swiper');

  readonly concernClass = input<string>(ApiClientConstant.Regimen.Class.FACE);
  readonly language = input<string>(ApiClientConstant.LanguageString.EN);
  readonly gender = input<string>(ApiClientConstant.User.Gender.FEMALE);
  readonly hideReadMore = input<boolean>(false);
  readonly decreaseHeaderGap = input<boolean>(false);
  readonly redirectToTestimonialPage = output<void>();

  private readonly conn = inject(ConnectionService);
  private readonly userService = inject(UserService);
  private readonly experimentService = inject(ExperimentService);

  private readonly apiTestimonials = toSignal(
    from(this.fetchTestimonials()).pipe(
      map((list): RebrandedTestimonialStory[] => list.map((item) => this.mapTestimonial(item))),
    ),
    { initialValue: [] as RebrandedTestimonialStory[] },
  );

  private readonly staticTestimonials = computed<RebrandedTestimonialStory[]>(() => {
    const regimenClass = ApiClientConstant.Regimen.Class;
    if (this.concernClass() === regimenClass.FACE) return RebrandedTestimonialStoriesObject[this.gender()][this.language()];
    if (this.concernClass() === regimenClass.HAIR) return RebrandedHairTestimonialObject[this.gender()][this.language()];
    return [];
  });

  private static readonly AVATAR_BG_CLASSES = ['tw-bg-teal-600', 'tw-bg-purple-400', 'tw-bg-blue-400'];

  readonly testimonials = computed<RebrandedTestimonialStory[]>(
    () => this.apiTestimonials().length ? this.apiTestimonials() : this.staticTestimonials()
  );

  readonly expandedIndices = signal<Set<number>>(new Set<number>());
  readonly collapsedCardHeightPx = 430;
  private readonly truncateCharLimit =
    this.userService.getUserLanguagePreference() === ApiClientConstant.LanguageString.EN ? 180 : 200;

  readonly swiperConfig: SwiperOptions = {
    slidesPerView: 1.2,
    spaceBetween: 0,
    autoHeight: false,
    loop: true,
    loopedSlides: 4,
    speed: 600,
    pagination: false,
    navigation: true,
    centeredSlides: false,
  };

  isExpanded(index: number): boolean {
    return this.expandedIndices().has(index);
  }

  avatarInitial(name: string): string {
    return (name ?? '').trim().charAt(0).toUpperCase();
  }

  avatarBgClass(index: number): string {
    const classes = TestimonialsWidgetComponent.AVATAR_BG_CLASSES;
    return classes[index % classes.length];
  }

  isTruncatable(text: string): boolean {
    return (text?.length ?? 0) > this.truncateCharLimit;
  }

  toggleExpanded(index: number): void {
    const next = new Set<number>(this.expandedIndices());
    if (next.has(index)) next.delete(index);
    else next.add(index);
    this.expandedIndices.set(next);
  }

  onSlideChange(): void {
    if (this.expandedIndices().size) this.expandedIndices.set(new Set<number>());
  }

  slideNext(): void {
    this.swiper()?.swiperRef.slideNext(100);
  }

  slidePrev(): void {
    this.swiper()?.swiperRef.slidePrev(100);
  }

  private async fetchTestimonials(): Promise<TestimonialResponseItem[]> {
    await this.experimentService.experimentsLoaded;
    if (!this.experimentService.isTestimonialsDecisionEngineEnabled()) return [];
    return this.conn.getTestimonials();
  }

  private mapTestimonial(item: TestimonialResponseItem): RebrandedTestimonialStory {
    return {
      testimonial: item.comment ?? '',
      name: item.username ?? '',
      location: [item.city, item.state].filter(Boolean).join(', '),
      userImg: item.thumbnailImage ?? '',
      testimonialImg: item.image ?? '',
      tags: item.displayTags ?? [],
    };
  }
}
